import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import "./animated-footer.css";

interface LinkItem {
  href: string;
  label: string;
  ariaLabel?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface AnimatedFooterProps {
  leftLinks: LinkItem[];
  rightLinks: LinkItem[];
  copyrightText: string | React.ReactNode;
  barCount?: number;
  leftChildren?: React.ReactNode;
  rightChildren?: React.ReactNode;
  footerHeading?: string;
}

const AnimatedFooter: React.FC<AnimatedFooterProps> = ({
  leftLinks,
  rightLinks,
  copyrightText,
  barCount = 23,
  leftChildren,
  rightChildren,
  footerHeading,
}) => {

  return (
    <footer
      className={cn(
        "relative w-full overflow-hidden footer-inverted mt-32"
      )}
    >

      {/* Footer Content */}
      <div className="relative px-6 py-8">
        <div className="mx-auto max-w-[1400px]">
          {/* Links Section */}
          <div className="flex flex-col md:flex-row justify-between gap-8 md:gap-12 lg:gap-24 xl:gap-32 mb-6">
            {/* Left Section */}
            <div className="flex flex-col gap-6 md:flex-1">
              {/* Footer Heading */}
              {footerHeading && (
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-medium footer-heading-inverted tracking-tight leading-tight mt-8">
                  {footerHeading}
                </h2>
              )}

              {/* CTA Section */}
              <div className="flex flex-col gap-4">
                {leftChildren}
              </div>

              {/* Social Links - Hidden on mobile, shown on desktop */}
              <div className="hidden md:flex flex-wrap gap-4 md:gap-6">
                {leftLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    aria-label={link.ariaLabel || link.label}
                    className={cn(
                      "text-xs md:text-sm font-medium transition-colors duration-200 footer-link-inverted"
                    )}
                    target={link.href.startsWith("http") ? "_blank" : undefined}
                    rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Right Section */}
            <div className="flex flex-col items-start md:items-end">
              {/* Clock */}
              <div className="mb-auto mt-4 md:mt-8">
                {rightChildren}
              </div>

              {/* Navigation Links */}
              <div className="flex flex-wrap justify-start md:justify-end gap-4 md:gap-6 mt-4">
                {rightLinks.map((link, index) => {
                  const isExternal = link.href.startsWith("http");
                  const isHash = link.href.startsWith("#");
                  const isRouterLink = !isExternal && !isHash;

                  const linkClasses = cn(
                    "text-xs md:text-sm font-medium transition-colors duration-200 footer-link-inverted",
                    index === 0 && "back-to-top-link"
                  );

                  if (isRouterLink) {
                    return (
                      <Link
                        key={index}
                        to={link.href}
                        aria-label={link.ariaLabel || link.label}
                        className={linkClasses}
                      >
                        {link.label}
                      </Link>
                    );
                  }

                  return (
                    <a
                      key={index}
                      href={link.href}
                      aria-label={link.ariaLabel || link.label}
                      className={linkClasses}
                      target={isExternal ? "_blank" : undefined}
                      rel={isExternal ? "noopener noreferrer" : undefined}
                      onClick={link.onClick}
                    >
                      {link.label}
                    </a>
                  );
                })}
              </div>

              {/* Social Links - Mobile only, below nav links */}
              <div className="md:hidden flex flex-wrap gap-4 mt-4">
                {leftLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    aria-label={link.ariaLabel || link.label}
                    className={cn(
                      "text-xs md:text-sm font-medium transition-colors duration-200 footer-link-inverted"
                    )}
                    target={link.href.startsWith("http") ? "_blank" : undefined}
                    rel={link.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Copyright */}
          <div className="text-center footer-copyright-border pt-6">
            <p className="text-sm footer-copyright-text">
              {copyrightText}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default AnimatedFooter;
